import React, { useState } from 'react';
import Pagination from '../../Common/Pagination';
import { CSVLink } from "react-csv";
import useExecFunctionOnBackButton from '../../Common/useExecFunctionOnBackButton'
import SectionHeader, { SectionHeaderTypes } from '../../Common/SectionHeader';

export default function SiteGradingLogEntries({ logTypeName, logs, onPrint, onCloseLogEntries }) {
    const dateFormatter = new Intl.DateTimeFormat('en-US', { day: "2-digit", month: "2-digit", year: "numeric" });
    const [selectedPage, setSelectedPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortedBy, setSortedBy] = useState('activitystarted');
    const [sortOrder, setSortOrder] = useState('desc');

    useExecFunctionOnBackButton(() => {
        window.history.go(1);
        onCloseLogEntries();
    });

    function handleSortByAndOrder(sortByAndOrder) {
        const [sortByParm, sortOrderParam] = sortByAndOrder.split("-");

        setSortOrder(sortOrderParam);
        setSortedBy(sortByParm);
    }

    function handleSortBy(sortBy) {
        let newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';

        setSortOrder(sortedBy === sortBy ? newSortOrder : 'desc');
        setSortedBy(sortBy);
    }

    function getSortComparer() {
        let isDesc = sortOrder === "desc";
        let compareFn = undefined;
        switch (sortedBy?.toLowerCase()) {
            case 'id':
                compareFn = (a, b) => (isDesc ? b.id - a.id : a.id - b.id);
                break;
            case 'description':
                compareFn = (a, b) => (isDesc ? ('' + b.description).localeCompare(a.description) : ('' + a.description).localeCompare(b.description));
                break;
            case 'location':
                compareFn = (a, b) => (isDesc ? ('' + b.location).localeCompare(a.location) : ('' + a.location).localeCompare(b.location));
                break;
            case 'duration':
                compareFn = (a, b) => (isDesc ? ('' + b.duration).localeCompare(a.duration) : ('' + a.duration).localeCompare(b.duration));
                break;
            case 'stabledescription':
                compareFn = (a, b) => (isDesc ? ('' + b.stableDescription).localeCompare(a.stableDescription) : ('' + a.stableDescription).localeCompare(b.stableDescription));
                break;
            case 'ownershipdate':
                compareFn = (a, b) => {
                    const timeA = new Date(a.ownershipDate).getTime();
                    const timeB = new Date(b.ownershipDate).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'stabilizationstarted':
                compareFn = (a, b) => {
                    const timeA = new Date(a.stabilizationStarted).getTime();
                    const timeB = new Date(b.stabilizationStarted).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'activityended':
                compareFn = (a, b) => {
                    const timeA = new Date(a.activityEnded).getTime();
                    const timeB = new Date(b.activityEnded).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
            case 'activitystarted':
            default:
                compareFn = (a, b) => {
                    const timeA = new Date(a.activityStarted).getTime();
                    const timeB = new Date(b.activityStarted).getTime();
                    return isDesc ? timeB - timeA : timeA - timeB;
                };
                break;
        }

        return compareFn;
    }

    function handleSelectPage(pageNum) {
        setSelectedPage(pageNum);
    }

    let shownStartIndex = (pageSize * selectedPage) - pageSize;
    let shownEndIndex = (pageSize * selectedPage);

    let csvHeaders = [
        { label: "Date Activity Initiated", key: "activityStarted" },
        { label: "Activity Description", key: "description" },
        { label: "Activity Location", key: "location" },
        { label: "Date Activity Ceased", key: "activityEnded" },
        { label: "Date Stabilization Measure Initiated", key: "stabilizationStarted" },
        { label: "Stabilization Measure Description", key: "stableDescription" },
        // { label: "Ownership Date", key: "ownershipDate" },
    ];

    let compareFn = getSortComparer(sortedBy, sortOrder);
    let sortedLogs = (logs && logs.toSorted(compareFn)) || [];

    let csvData = sortedLogs && sortedLogs.map(log => ({
        activityStarted: log.activityStarted ? dateFormatter.format(new Date(log.activityStarted)) : "",
        description: log.description,
        location: log.location,
        activityEnded: log.activityEnded ? dateFormatter.format(new Date(log.activityEnded)) : "",
        stabilizationStarted: log.stabilizationStarted ? dateFormatter.format(new Date(log.stabilizationStarted)) : "",
        stableDescription: log.stableDescription,
        // ownershipDate: log.ownershipDate ? dateFormatter.format(new Date(log.ownershipDate)) : "",
    }));

    let logListItems = sortedLogs && sortedLogs
        .map((log, index) => <tr className={index < shownStartIndex || index >= shownEndIndex ? "hidden" : ""} key={log.id}>
            <td className="tabular-nums">{log.activityStarted && dateFormatter.format(new Date(log.activityStarted))}</td>
            <td>{log.description}</td>
            <td>{log.location}</td>
            <td className="tabular-nums">{log.activityEnded && dateFormatter.format(new Date(log.activityEnded))}</td>
            <td>{log.duration}</td>
            <td className="tabular-nums">{log.stabilizationStarted && dateFormatter.format(new Date(log.stabilizationStarted))}</td>
            <td>{log.stableDescription}</td>
            {/*<td className="tabular-nums">{log.ownershipDate && dateFormatter.format(new Date(log.ownershipDate))}</td>*/}
            <td className="smallScreen">
                <div className="log">
                    {log.activityStarted && (<div>
                        <span className="fieldDesc">Initiated On:</span>
                        {log.activityStarted && dateFormatter.format(new Date(log.activityStarted))}
                    </div>)}
                    {log.activityEnded && (<div>
                        <span className="fieldDesc">Ceased On:</span>
                        {log.activityEnded && dateFormatter.format(new Date(log.activityEnded))}
                    </div>)}
                    {log.duration && (<div>
                        <span className="fieldDesc">Stoppage:</span>
                        {log.duration}
                    </div>)}
                    {log.description && (<div>
                        <span className="fieldDesc">Activity Description:</span>
                        <div className="desc">{log.description}</div>
                    </div>)}
                    {log.location && (<div>
                        <span className="fieldDesc">Activity Location:</span>
                        <div className="desc">{log.location}</div>
                    </div>)}
                    {log.stabilizationStarted && (<div>
                        <span className="fieldDesc">Stabilization Measure On:</span>
                        {log.stabilizationStarted && dateFormatter.format(new Date(log.stabilizationStarted))}
                    </div>)}
                    {log.stableDescription && (<div>
                        <span className="fieldDesc">Stabilization Measure:</span>
                        <div className="desc">{log.stableDescription}</div>
                    </div>)}
                    {/*
                    {log.ownershipDate && (<div>
                        <span className="fieldDesc">Ownership Date:</span>
                        {log.ownershipDate && dateFormatter.format(new Date(log.ownershipDate))}
                    </div>)}
                    */}
                </div>
            </td>
        </tr>);

    if (selectedPage > 1 && (pageSize * selectedPage) - logListItems.length > 0) {
        let emptyRows = Array.from(
            { length: (pageSize * selectedPage) - logListItems.length },
            (cv, i) => (<tr className="noBkg noPrint" key={`R${i}`}><td>&nbsp;</td></tr>));
        logListItems = logListItems.concat(emptyRows);
    }

    let printButton = (<button onClick={onPrint}>Print</button>);
    let exportButton = (<CSVLink data={csvData} headers={csvHeaders} filename={`${logTypeName}.csv`} className="button" target="_blank">Export</CSVLink>);

    let sortSelect = (<select className="bg-drhPalette-secondary " onChange={(e) => handleSortByAndOrder(e.target.value)} value={`${sortedBy}-${sortOrder}`}>
        <option className="checked:font-semibold checked:text-white" value='activitystarted-desc'>Activity Initiated desc</option>
        <option className="checked:font-semibold checked:text-white" value='activitystarted-asc'>Activity Initiated asc</option>
        <option className="checked:font-semibold checked:text-white" value='description-desc'>Activity Description desc</option>
        <option className="checked:font-semibold checked:text-white" value='description-asc'>Activity Description asc</option>
        <option className="checked:font-semibold checked:text-white" value='location-desc'>Activity Location desc</option>
        <option className="checked:font-semibold checked:text-white" value='location-asc'>Activity Location asc</option>
        <option className="checked:font-semibold checked:text-white" value='activityended-desc'>Activity Ceased desc</option>
        <option className="checked:font-semibold checked:text-white" value='activityended-asc'>Activity Ceased asc</option>
        <option className="checked:font-semibold checked:text-white" value='duration-desc'>Stoppage desc</option>
        <option className="checked:font-semibold checked:text-white" value='duration-asc'>Stoppage asc</option>
        <option className="checked:font-semibold checked:text-white" value='stabilizationstarted-desc'>Measure Initiated desc</option>
        <option className="checked:font-semibold checked:text-white" value='stabilizationstarted-asc'>Measure Initiated asc</option>
        <option className="checked:font-semibold checked:text-white" value='stabledescription-desc'>Measure Description desc</option>
        <option className="checked:font-semibold checked:text-white" value='stabledescription-asc'>Measure Description asc</option>
        {/*<option className="checked:font-semibold checked:text-white" value='ownershipdate-desc'>Ownership Date desc</option>*/}
        {/*<option className="checked:font-semibold checked:text-white" value='ownershipdate-asc'>Ownership Date asc</option>*/}
    </select>);

    let sortBarSmallScreen = (<SectionHeader classes="selectAllSmallScreen m-1 pl-0.5 justify-end md:hidden" sectionHeaderType={SectionHeaderTypes.Secondary}>
        <div className="text-base">
            <span className="hidden xsm:inline">Sort by</span>
            {sortSelect}
        </div>
    </SectionHeader>);

    let sortIconClass = sortOrder === 'desc' ? 'dhicon-down-open' : 'dhicon-up-open';

    //let sortableIcon = (<span className="flex"><i className="dhicon-up-open relative -top-1" /><i className="dhicon-down-open relative top-[3px] -left-[22.5px]" /></span>);
    //let sortableIcon2 = (<span className="flex"><i className="dhicon-up-open relative top-2" /><i className="dhicon-down-open relative top-4 -left-[22.5px]" /></span>);
    //let sortableColDescClass = "relative -left-[22px]";
    let sortableIcon = (<div className="flex"><i className="iconUpDown" /></div>);
    let sortableIcon2 = (<div className="flex"><i className="iconUpDown" /></div>);
    let sortableColDescClass = "";

    let activityStartedHeader = (<th onClick={() => handleSortBy('activitystarted')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'activitystarted' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'activitystarted' ? sortableColDescClass : ''}>Date Activity</span>
                <br />
                <span className={sortedBy !== 'activitystarted' ? sortableColDescClass : ''}>Initiated</span></div>
        </div>
    </th>);

    let activityEndedHeader = (<th onClick={() => handleSortBy('activityended')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'activityended' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'activityended' ? sortableColDescClass : ''}>Date Activity</span>
                <br />
                <span className={sortedBy !== 'activityended' ? sortableColDescClass : ''}>Ceased</span>
            </div>
        </div>
    </th>);

    let stabilizationStartedHeader = (<th onClick={() => handleSortBy('stabilizationstarted')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'stabilizationstarted' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'stabilizationstarted' ? sortableColDescClass : ''}>Date Stabilization</span>
                <br />
                <span className={sortedBy !== 'stabilizationstarted' ? sortableColDescClass : ''}>Measure Initiated</span>
            </div>
        </div>
    </th>);

    let stabilizationDescHeader = (<th onClick={() => handleSortBy('stabledescription')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'stabledescription' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'stabledescription' ? sortableColDescClass : ''}>Stabilization Measure</span>
                <br />
                <span className={sortedBy !== 'stabledescription' ? sortableColDescClass : ''}>Description</span>
            </div>
        </div>
    </th>);

    let ownershipDateHeader = (<th onClick={() => handleSortBy('ownershipdate')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'ownershipdate' ? (<i className={`content-center ${sortIconClass}`} />) : (sortableIcon2)}
            <div>
                <span className={sortedBy !== 'ownershipdate' ? sortableColDescClass : ''}>Ownership</span>
                <br />
                <span className={sortedBy !== 'ownershipdate' ? sortableColDescClass : ''}>Date</span>
            </div>
        </div>
    </th>);

    let activityDescHeader = (<th onClick={() => handleSortBy('description')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'description' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortedBy !== 'description' ? sortableColDescClass : ''}>Activity Description</span>
        </div>
    </th>);

    let locationHeader = (<th onClick={() => handleSortBy('location')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'location' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortedBy !== 'location' ? sortableColDescClass : ''}>Activity Location</span>
        </div>
    </th>);

    let durationHeader = (<th onClick={() => handleSortBy('duration')} className="cursor-pointer">
        <div className="flex">
            {sortedBy === 'duration' ? (<i className={sortIconClass} />) : (sortableIcon)}
            <span className={sortedBy !== 'duration' ? sortableColDescClass : ''}>Stoppage</span>
        </div>
    </th>);

    let logList = logListItems.length === 0 ? undefined : (<>
        {sortBarSmallScreen}
        <table id="logEntries" className="itemList">
            <thead>
                <tr>
                    {activityStartedHeader}
                    {activityDescHeader}
                    {locationHeader}
                    {activityEndedHeader}
                    {durationHeader}
                    {stabilizationStartedHeader}
                    {stabilizationDescHeader}
                    {/*{ownershipDateHeader}*/}
                </tr>
            </thead>
            <tbody>{logListItems}</tbody>
        </table>
        <Pagination itemCount={sortedLogs.length} pageSize={pageSize} selectedPage={selectedPage} hideWhenSinglePage={false} onSelectPage={handleSelectPage}>
            <div className="flex gap-x-1">
            </div>
        </Pagination>
    </>);

    let logTypeNameDescription = (<div className="logType">
        <div className="flexbox-flex align-start overflow-x-hidden">
            <span className="nameContainer">
                <span className="name">Log Entries - {logTypeName}</span>
            </span>
        </div>
    </div>);

    return (
        <div className="logTypeContainer">
            {logTypeNameDescription}
            {logList}
        </div>
    );
}